import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../buttons/IconButton';

/**
 * The heatmap's three metric controls: tile size, colour coding and grouping.
 *
 * TD-2720 — these buttons existed twice, in markup that had drifted: a desktop
 * copy inside `renderProfilerResultsHeader` (SearchResults/index.jsx) and a mobile
 * copy in <ProfilerHeatmapView/>. The stacked heatmap gives them a single home
 * above the tiles, so the two copies collapse into this one component.
 *
 * TD-2733 — moved out of the search page so the saved-project and team-feedback
 * surfaces render the same three controls instead of their own copies. The prop is
 * named `controls` rather than `state` because it is a view-controls bag, not a page
 * store: search and saved projects pass their MobX store, team feedback passes the
 * return value of `useProfilerViewControls`.
 *
 * `layout` only decides whether each control gets a `.control-row` wrapper — mobile
 * wraps the controls onto separate rows and anchors each dropdown to its own row,
 * while desktop lays them out inline and anchors the dropdowns to the shared
 * `.profiler-heatmap-controls-header` (the positioned ancestor in both cases).
 */
const ProfilerHeatmapControls = observer(({ controls, isLoading, hasResults, layout }) => {
  const isMobile = layout === 'mobile';
  const disabled = isLoading || !hasResults;

  // On mobile each control owns a positioned row; on desktop the wrapper stays
  // class-less and static, so the menus keep resolving against the controls header.
  const rowClass = isMobile ? 'control-row' : '';

  const tileSizeName =
    controls.profilerHeatmapTileSizeOptions.find(
      (option) => option.value === controls.profilerHeatmapTileSize
    )?.name || controls.profilerHeatmapTileSize;

  const colorCodingName =
    controls.profilerHeatmapColorCodingOptions.find(
      (option) => option.value === controls.profilerHeatmapColorCoding
    )?.name || controls.profilerHeatmapColorCoding;

  return (
    <div className={`profiler-heatmap-controls-header${isMobile ? ' mobile-view' : ''}`}>
      <div className={rowClass}>
        <IconButton
          id="profiler-tile-size-metric"
          innerText={
            <>
              Tile size: <font className="bold">{tileSizeName}</font>
            </>
          }
          icon={
            controls.isProfilerHeatmapTileSizeMenuToggled
              ? ICON_BUTTON_ICONS.CHEVRON_UP
              : ICON_BUTTON_ICONS.CHEVRON_DOWN
          }
          iconOnRight
          type={ICON_BUTTON_TYPES.BLUE}
          disabled={disabled}
          onClick={controls.toggleProfilerHeatmapTileSizeMenu}
          filled={controls.isProfilerHeatmapTileSizeMenuToggled}
        />
        {controls.isProfilerHeatmapTileSizeMenuToggled && (
          <div className="heatmap-control-menu-wrap tile-size">
            {controls.profilerHeatmapTileSizeOptions.map((option) => (
              <IconButton
                key={option.value}
                hoverType={ICON_BUTTON_TYPES.BLUE}
                withBorder={false}
                innerText={option.name}
                onClick={() =>
                  controls.setProfilerHeatmapTileSize(option.value) &
                  controls.toggleProfilerHeatmapTileSizeMenu()
                }
                disabled={controls.profilerHeatmapTileSize === option.value}
                filled={controls.profilerHeatmapTileSize === option.value}
              />
            ))}
          </div>
        )}
      </div>
      <div className={rowClass}>
        <IconButton
          id="profiler-color-coding-metric"
          innerText={
            <>
              Color coding: <font className="bold">{colorCodingName}</font>
            </>
          }
          icon={
            controls.isProfilerHeatmapColorCodingMenuToggled
              ? ICON_BUTTON_ICONS.CHEVRON_UP
              : ICON_BUTTON_ICONS.CHEVRON_DOWN
          }
          iconOnRight
          type={ICON_BUTTON_TYPES.BLUE}
          disabled={disabled}
          onClick={controls.toggleProfilerHeatmapColorCodingMenu}
          filled={controls.isProfilerHeatmapColorCodingMenuToggled}
        />
        {controls.isProfilerHeatmapColorCodingMenuToggled && (
          <div className="heatmap-control-menu-wrap color-coding">
            {controls.profilerHeatmapColorCodingOptions.map((option) => (
              <IconButton
                key={option.value}
                hoverType={ICON_BUTTON_TYPES.BLUE}
                withBorder={false}
                innerText={option.name}
                onClick={() =>
                  controls.setProfilerHeatmapColorCoding(option.value) &
                  controls.toggleProfilerHeatmapColorCodingMenu()
                }
                disabled={controls.profilerHeatmapColorCoding === option.value}
                filled={controls.profilerHeatmapColorCoding === option.value}
              />
            ))}
          </div>
        )}
      </div>
      {!controls.disableProfilerHeatmapGroup && (
        <div className={rowClass}>
          <IconButton
            id="profiler-grouping-toggle"
            type={ICON_BUTTON_TYPES.BLUE}
            icon={
              controls.isProfilerHeatmapGroupingToggled
                ? ICON_BUTTON_ICONS.TOGGLE_ON
                : ICON_BUTTON_ICONS.TOGGLE_OFF
            }
            innerText="Grouping"
            onClick={controls.toggleProfilerHeatmapGrouping}
            filled={controls.isProfilerHeatmapGroupingToggled}
            disabled={disabled || controls.disableProfilerHeatmapGroup}
          />
        </div>
      )}
    </div>
  );
});

ProfilerHeatmapControls.propTypes = {
  controls: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  hasResults: PropTypes.bool,
  layout: PropTypes.oneOf(['desktop', 'mobile'])
};

export default ProfilerHeatmapControls;
